/* ------------------------------------------------------------------
  File: Pagination.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable pagination component for the Accend Holdings website.
//   - Supports page navigation, sibling pages, ellipsis, previous/next controls,
//     first/last controls, disabled states, and accessible labels.
//   - Suitable for newsroom articles, projects, careers, resources,
//     investor documents, search results, and paginated dynamic content.

/**
 * Pagination
 *
 * A reusable pagination component.
 *
 * @param {number} currentPage
 * The currently active page.
 *
 * @param {number} totalPages
 * Total number of pages.
 *
 * @param {Function} onPageChange
 * Function called with the selected page number.
 *
 * @param {number} [siblingCount=1]
 * Number of sibling pages shown around the current page.
 *
 * @param {boolean} [showFirstLast=true]
 * Controls whether first and last buttons are shown.
 *
 * @param {boolean} [showPrevNext=true]
 * Controls whether previous and next buttons are shown.
 *
 * @param {"default" | "compact"} [variant="default"]
 * Controls the pagination display style.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";
import "./Pagination.css";

const DOTS = "dots";

const range = (start, end) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, index) => index + start);
};

const getPaginationRange = ({ currentPage, totalPages, siblingCount }) => {
  const totalPageNumbers = siblingCount + 5;

  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = range(1, leftItemCount);

    return [...leftRange, DOTS, lastPageIndex];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = range(totalPages - rightItemCount + 1, totalPages);

    return [firstPageIndex, DOTS, ...rightRange];
  }

  const middleRange = range(leftSiblingIndex, rightSiblingIndex);

  return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
};

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  siblingCount = 1,
  showFirstLast = true,
  showPrevNext = true,
  variant = "default",
  className = "",
  ...props
}) => {
  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);

  const paginationRange = getPaginationRange({
    currentPage: safeCurrentPage,
    totalPages,
    siblingCount,
  });

  const paginationClasses = [
    "acc-pagination",
    `acc-pagination--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handlePageChange = (page) => {
    if (
      page < 1 ||
      page > totalPages ||
      page === safeCurrentPage ||
      typeof onPageChange !== "function"
    ) {
      return;
    }

    onPageChange(page);
  };

  const isFirstPage = safeCurrentPage === 1;
  const isLastPage = safeCurrentPage === totalPages;

  return (
    <nav
      className={paginationClasses}
      role="navigation"
      aria-label="Pagination"
      {...props}
    >
      <ul className="acc-pagination__list">
        {showFirstLast && (
          <li className="acc-pagination__item">
            <button
              type="button"
              className="acc-pagination__button"
              aria-label="Go to first page"
              disabled={isFirstPage}
              onClick={() => handlePageChange(1)}
            >
              <LuChevronsLeft />
            </button>
          </li>
        )}

        {showPrevNext && (
          <li className="acc-pagination__item">
            <button
              type="button"
              className="acc-pagination__button"
              aria-label="Go to previous page"
              disabled={isFirstPage}
              onClick={() => handlePageChange(safeCurrentPage - 1)}
            >
              <LuChevronLeft />
            </button>
          </li>
        )}

        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <li
                key={`dots-${index}`}
                className="acc-pagination__item"
                aria-hidden="true"
              >
                <span className="acc-pagination__ellipsis">…</span>
              </li>
            );
          }

          const isActive = pageNumber === safeCurrentPage;

          return (
            <li key={pageNumber} className="acc-pagination__item">
              <button
                type="button"
                className={[
                  "acc-pagination__button",
                  isActive ? "acc-pagination__button--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-label={`Go to page ${pageNumber}`}
                aria-current={isActive ? "page" : undefined}
                disabled={isActive}
                onClick={() => handlePageChange(pageNumber)}
              >
                {pageNumber}
              </button>
            </li>
          );
        })}

        {showPrevNext && (
          <li className="acc-pagination__item">
            <button
              type="button"
              className="acc-pagination__button"
              aria-label="Go to next page"
              disabled={isLastPage}
              onClick={() => handlePageChange(safeCurrentPage + 1)}
            >
              <LuChevronRight />
            </button>
          </li>
        )}

        {showFirstLast && (
          <li className="acc-pagination__item">
            <button
              type="button"
              className="acc-pagination__button"
              aria-label="Go to last page"
              disabled={isLastPage}
              onClick={() => handlePageChange(totalPages)}
            >
              <LuChevronsRight />
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default Pagination;